import { Link, useSearch } from '@tanstack/react-router';

import { DateRangeFilter } from '@/components/admin/date-range-filter';
import { OrderStatusBadge, QuoteStatusBadge, formatDate } from '@/components/status-timeline';
import { Badge, Callout, EmptyState } from '@/components/ui';
import { headlineTiles, visibleStats } from '@/lib/dashboard-stats';
import { formatCents } from '@/lib/money';
import { ORDER_STATUS_LABEL, type OrderStatus } from '@/lib/types';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { adminDashboardQuery, adminPurchaseOrdersQuery, adminShellQuery } from '@/queries';

/**
 * The operations dashboard. AC-5.1, prototype screenshot 6-dashboard.
 *
 * Density 6 here rather than the storefront's 4: this is a working screen, so
 * the numbers get room and everything else gets out of the way. Cost and profit
 * come from `base_cost_cents` against `retail_price_cents`, which is the only
 * pair that gives a true margin.
 *
 * **Who may see which tile is in `dashboard-stats.ts`, not here.** This screen
 * showed the margin to anybody internal, which is every staff member, because
 * its nav entry declares `module: null` and nothing on it asked a second
 * question (R-235). The rule lives in a function so a test can falsify it.
 */

/** The grid track for a given number of visible tiles. See the `dl` below. */
const STAT_TRACK: Record<number, string> = {
  1: '',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
};

const RANGES: Record<string, { label: string; days: number | null }> = {
  '7d': { label: 'Last 7 days', days: 7 },
  '30d': { label: 'Last 30 days', days: 30 },
  '90d': { label: 'Last 90 days', days: 90 },
  all: { label: 'All time', days: null },
};

/**
 * How many purchase orders are late. Spec 012, FR-1244. The same list and
 * the same rule as Purchase orders, Late: open, and the expected date has
 * passed. Only for somebody who may see purchasing.
 */
function LatePurchaseOrders() {
  const late = useQuery({ ...adminPurchaseOrdersQuery('late', 1), retry: false });
  if (!late.data) return null;
  const n = late.data.total;
  return (
    <p className="mt-4 text-[14px]">
      {n === 0 ? 'No purchase order is late.' : (
        <Link to="/admin/purchase-orders" search={{ list: 'late' }} className="font-semibold text-brand-ink hover:underline">
          {n} {n === 1 ? 'purchase order is' : 'purchase orders are'} late
        </Link>
      )}
    </p>
  );
}

export default function AdminDashboard() {
  const search = useSearch({ strict: false }) as { range?: string };
  const rangeKey = search.range ?? '30d';
  const range = RANGES[rangeKey] ?? RANGES['30d']!;

  /**
   * The figures are computed BY THE DATABASE, not by this screen. R-241.
   *
   * The other shape of this file summed `orders` in the browser. That reads
   * whatever page of orders the caller was given, so with 12,655 orders behind
   * a bounded read it showed $50 where the answer was $600,921. It also needs
   * `items.avg_cost_cents`, which is revoked from `authenticated`, so the cost
   * half answered "permission denied for table items".
   *
   * `dashboard_summary()` counts every row once, in one pass, and decides
   * server side whether this caller may see cost at all.
   */
  const { summary, quotes } = useSuspenseQuery(adminDashboardQuery(rangeKey)).data;
  // Already in the cache: the admin shell loads it before this screen renders.
  const { can } = useSuspenseQuery(adminShellQuery).data;

  const inRangeCount = summary.orders;

  const byStatus = Object.fromEntries(
    summary.statuses.map((s) => [s.status, Number(s.n)]),
  ) as Record<string, number>;


  const awaiting = quotes.filter(
    (q) => q.status === 'submitted' || q.status === 'proof_sent',
  );

  /**
   * Which tiles say what, and who may see them. Both rules are in
   * `dashboard-stats.ts`. Neither is here, and that is the point.
   *
   * **The wording is a rule, not decoration.** Cost and profit describe the
   * COSTED PORTION of the revenue, which is not the figure on the Revenue tile
   * beside them. The captions have to name that portion in dollars, or the
   * three cards cannot be reconciled by the person reading them. Guy subtracted
   * the first from the second, got a third answer, and reported the screen. He
   * was right to. Tracker #9.
   *
   * It was written out here, inline, where nothing in the suite could read it.
   * That is the same reason the permission rule had to move out (R-235).
   *
   * The figures are mine and the gate is theirs, and both are needed. Revenue
   * is a report figure and `reports` is a module the client's matrix already
   * carries, which staff hold false. Cost and profit are `costs`.
   *
   * `summary.show_cost` is the server's answer to the same question. Both stay:
   * the server decides what to SEND, this decides what to DRAW, and a tile with
   * no figure behind it is not drawn either way.
   */
  const stats = visibleStats(
    headlineTiles({
      orders: inRangeCount,
      revenue_cents: summary.revenue_cents,
      gst_cents: summary.gst_cents ?? null,
      cost_cents: summary.cost_cents,
      costed_revenue_cents: summary.costed_revenue_cents,
      show_cost: summary.show_cost,
      quotes_awaiting: awaiting.length,
    }),
    can,
  ).slice(0, 4);

  return (
    <div>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Dashboard</h1>
          <p className="mt-1 text-[12.5px] text-muted">{range.label}</p>
        </div>
        <DateRangeFilter
          current={rangeKey}
          options={Object.entries(RANGES).map(([k, v]) => ({ key: k, label: v.label }))}
        />
      </div>

      {/* The first screen anybody sees. With nothing loaded it showed six zeroes
          and no explanation, which reads as a broken system rather than an empty
          one. R-172. */}
      {inRangeCount === 0 && quotes.length === 0 && (
        <div className="mb-7">
          <Callout tone="brand" title="Nothing has been loaded yet">
            This is a working system with an empty database, not a broken one. The
            catalogue, the rate card, the machines and the storage layout each load
            with one command once the files arrive, and every screen fills the moment
            they do. Until then the shop shows your brand with nothing in it, and the
            studio tells customers to call.
          </Callout>
        </div>
      )}

      {/* No card chrome around the headline numbers: hairlines and space do the
          grouping, so the figures read first. */}
      {/* The track follows the tile count. A four-column grid holding one tile
          draws three empty cells in the line colour, which reads as a broken
          panel rather than as a person who may not see the other three.
          Written out rather than interpolated, because Tailwind scans for
          whole class names. */}
      <dl
        className={`grid gap-px overflow-hidden rounded-[10px] border border-line bg-line ${
          STAT_TRACK[stats.length] ?? STAT_TRACK[4]
        }`}
      >
        {stats.map((s) => (
          <div key={s.label} className="@container bg-panel px-5 py-6">
            <dt className="text-sm font-semibold text-muted">{s.label}</dt>
            {/* 26 px until the figure would not fit, then smaller. All time
                read "$1,315,530.6" at a fixed 26 px: the last cent ran under
                the next tile. A mono digit here is about 0.6 em wide, so the
                size is the tile width over the character count. Tracker #9. */}
            <dd
              className="mt-1.5 font-mono font-extrabold tracking-tight whitespace-nowrap"
              style={{ fontSize: `min(26px, calc(100cqi / ${s.value.length * 0.6}))` }}
            >
              {s.value}
            </dd>
            <dd className="mt-0.5 text-[13px] text-muted">{s.sub}</dd>
          </div>
        ))}
      </dl>

      {(can.purchasing ?? []).includes('view') && <LatePurchaseOrders />}

      <div className="mt-10 grid gap-10 lg:grid-cols-2">
        <section aria-labelledby="queue-heading">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h2 id="queue-heading" className="text-lg font-bold tracking-[-0.02em]">
              Quotes awaiting action
            </h2>
            <Link to="/admin/quotes" className="text-sm font-semibold text-brand-ink hover:underline">
              All quotes
            </Link>
          </div>
          {awaiting.length === 0 ? (
            <EmptyState title="Queue is clear" body="No quotes are waiting on you." />
          ) : (
            <ul className="divide-y divide-line overflow-hidden rounded-[10px] border border-line bg-panel">
              {awaiting.slice(0, 6).map((q) => (
                <li key={q.id}>
                  <Link
                    to="/admin/quotes/$ref" params={{ ref: q.ref }}
                    className="flex items-center gap-4 px-4 py-3.5 hover:bg-raised"
                 >
                    <span className="font-mono text-sm font-bold text-brand-ink">{q.ref}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold">
                        {q.contact.company ?? q.contact.name}
                      </span>
                      <span className="text-sm text-muted">
                        {q.qty} units &middot; {formatDate(q.created_at)}
                      </span>
                    </span>
                    {q.app_originated && <Badge tone="brand">App</Badge>}
                    <QuoteStatusBadge status={q.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-labelledby="pipeline-heading">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h2 id="pipeline-heading" className="text-lg font-bold tracking-[-0.02em]">
              Orders by status
            </h2>
            {/* "Pipeline" until the board and the register became two screens.
                This strip counts every status, including delivered, so it
                belongs to the register and the label now says so. */}
            <Link to="/admin/orders" className="text-sm font-semibold text-brand-ink hover:underline">
              All orders
            </Link>
          </div>
          <div className="overflow-hidden rounded-[10px] border border-line bg-panel">
            {(Object.keys(ORDER_STATUS_LABEL) as OrderStatus[])
              .filter((s) => (byStatus[s] ?? 0)> 0)
              .map((status) => {
                const count = byStatus[status] ?? 0;
                const pct = inRangeCount> 0 ? (count / inRangeCount) * 100 : 0;
                return (
                  <div key={status} className="flex items-center gap-4 border-b border-line px-4 py-3 last:border-b-0">
                    <span className="w-32 shrink-0">
                      <OrderStatusBadge status={status} />
                    </span>
                    {/* Bare bar, no filled track: this is a proportion, not a gauge. */}
                    <span aria-hidden className="h-1.5 flex-1 rounded-full bg-brand/25">
                      <span
                        className="block h-full rounded-full bg-brand-strong"
                        style={{ width: `${pct}%` }}
                      />
                    </span>
                    <span className="w-8 text-right font-mono text-[15px] font-bold">
                      {count}
                    </span>
                  </div>
                );
              })}
            {inRangeCount === 0 && (
              <p className="px-4 py-8 text-center text-[15px] text-muted">
                No orders in this range.
              </p>
            )}
          </div>
        </section>
      </div>

      {/*
        * WHERE THE ORDERS CAME FROM. Spec 006 FR-610.
        *
        * The form asks this on every order taken by hand, and a field that is
        * asked for and never reported on is a field people stop filling in
        * honestly. It is the number the marketing spend is judged by: Guy pays
        * for Facebook and Instagram and had no way to see what either returned.
        *
        * `reports` gates it, the same module that gates the revenue tile. The
        * money on this panel is the same money.
        */}
      {(can.reports ?? []).includes('view') && (summary.sources ?? []).length > 0 && (
        <section className="mt-10" aria-labelledby="sources-heading">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h2 id="sources-heading" className="text-lg font-bold tracking-[-0.02em]">
              Where the orders came from
            </h2>
          </div>
          <div className="overflow-x-auto rounded-[10px] border border-line bg-panel">
            <table className="dt w-full min-w-[420px] text-left">
              <thead>
                <tr>
                  <th scope="col">Source</th>
                  <th scope="col" className="num">Orders</th>
                  <th scope="col" className="num">Goods</th>
                </tr>
              </thead>
              <tbody>
                {(summary.sources ?? []).map((row) => (
                  <tr key={row.code ?? 'not-recorded'}>
                    <td className={row.code ? '' : 'text-muted'}>
                      {row.label}
                      {/*
                        * Say why the big row is big, on the row itself. It is
                        * every order imported from Shopify, and without this
                        * line it reads as a failure to fill the form in.
                        */}
                      {!row.code && (
                        <span className="block text-[12.5px] text-muted">
                          Orders from before the studio started recording this,
                          and every order the shop took.
                        </span>
                      )}
                    </td>
                    <td className="num font-mono font-bold">
                      {Number(row.orders).toLocaleString('en-AU')}
                    </td>
                    <td className="num font-mono font-bold">
                      {formatCents(Number(row.revenue_cents))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="mt-10" aria-labelledby="recent-heading">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id="recent-heading" className="text-lg font-bold tracking-[-0.02em]">
            Recent orders
          </h2>
        </div>
        <div className="overflow-x-auto rounded-[10px] border border-line bg-panel">
          <table className="dt w-full min-w-[640px] text-left">
            <thead>
              <tr>
                <th scope="col">Reference</th>
                <th scope="col">Placed</th>
                <th scope="col">Channel</th>
                <th scope="col">Status</th>
                <th scope="col" className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {summary.recent.map((o) => (
                <tr key={o.id}>
                <td>
                    <Link
                      to="/admin/orders" search={{ ref: o.ref }}
                      className="font-mono text-sm font-bold text-brand-ink hover:underline"
                   >
                      {o.ref}
                    </Link>
                  </td>
                <td className="text-muted">{formatDate(o.created_at)}</td>
                <td>
                    {o.channel === 'web' ? (
                      <span className="text-muted">Web</span>
                    ) : (
                      <Badge tone="brand">{o.channel.toUpperCase()}</Badge>
                    )}
                  </td>
                <td>
                    <OrderStatusBadge status={o.status} />
                  </td>
                <td className="num font-mono font-bold">
                    {formatCents(o.total_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
